import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, KeyRound, ShieldCheck } from 'lucide-react';
import { useAuth } from '../features/auth/AuthContext';
import { BrandLogo, Button, Card, CardBody, CardHeader, Input, LoadingSpinner, PageHeader } from '@examflow/ui';
import { BeianFooter } from '../components/BeianFooter';
import { buildAccessState } from '../features/access/accessStateModel';
import { canAccessExam } from '@examflow/learning-space';
import { buildLoginRedirectForRedeem, sanitizeAuthRedirect } from '../features/auth/authRedirects';

function resolveRedirectExamType(redirectTo: string): string | null {
  try {
    const url = new URL(redirectTo, 'https://examflow.local');
    const examType = url.searchParams.get('examType')?.trim().toLowerCase();
    return examType || null;
  } catch {
    return null;
  }
}

function RedeemPage() {
  const { redeemCode, entitlements, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: { pathname?: string; search?: string } } | null;
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = sanitizeAuthRedirect(
    new URLSearchParams(location.search).get('redirect') ||
      (locationState?.from?.pathname
        ? `${locationState.from.pathname}${locationState.from.search ?? ''}`
        : null) ||
      '/'
  );
  const redirectExamType = resolveRedirectExamType(redirectTo);
  const alreadyHasTargetExam = Boolean(
    redirectExamType && canAccessExam(entitlements, redirectExamType)
  );
  const hasEntitlement = Boolean(entitlements?.grants.length);
  const hasActiveEntitlement = Boolean(
    entitlements?.grants.some((grant) => !grant.expiresAt || new Date(grant.expiresAt) > new Date())
  );
  const accessState = buildAccessState({
    hasEntitlement,
    isExpired: hasEntitlement && !hasActiveEntitlement,
  });

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-page">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate to={buildLoginRedirectForRedeem(redirectTo)} state={{ from: location }} replace />
    );
  }

  // Already entitled for the exam this redeem was meant to unlock — don't trap the learner here.
  if (alreadyHasTargetExam) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await redeemCode(code);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : '兑换失败，请检查兑换码';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-page">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:px-8">
        <section className="space-y-6">
          <div className="mb-2">
            <BrandLogo variant="full" size={28} />
          </div>
          <PageHeader
            eyebrow="兑换开通"
            title={accessState.title}
            description={accessState.description}
          />

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {['兑换后自动绑定考试训练包', '进入诊断和训练流程', '学习进度保留在当前账户'].map(
              (item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-lg border border-sage-100 bg-bg-surface p-4"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-state-success" />
                  <p className="text-sm leading-6 text-ink-secondary">{item}</p>
                </div>
              )
            )}
          </div>
        </section>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-row items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-sage-50 text-primary ring-1 ring-sage-100">
              <KeyRound className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink-primary">兑换授权</h2>
              <p className="text-sm text-ink-muted">一个兑换码对应一个考试训练包</p>
            </div>
          </CardHeader>

          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-4">
              {hasActiveEntitlement && accessState.alreadyEntitledHint ? (
                <div className="space-y-3 rounded-md bg-sage-50 p-4 text-sm leading-6 text-ink-secondary">
                  <p className="text-state-success">{accessState.alreadyEntitledHint}</p>
                  {accessState.secondaryActionLabel ? (
                    <Link
                      to={redirectTo}
                      className="inline-flex items-center gap-2 font-medium text-primary hover:text-primary-hover"
                    >
                      {accessState.secondaryActionLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : null}
                </div>
              ) : null}

              <Input
                label="兑换码"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="粘贴你的兑换码"
                className="py-3 text-base uppercase tracking-wider placeholder:normal-case placeholder:tracking-normal"
                autoComplete="one-time-code"
                error={error}
              />

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={!code.trim()}
                isLoading={submitting}
              >
                {submitting ? '兑换中...' : accessState.primaryActionLabel}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </Button>

              <div className="flex items-start gap-2 rounded-md bg-bg-muted p-3 text-sm leading-6 text-ink-muted">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
                <p>兑换码用于开启考试空间。已有权限时仍可继续兑换，开通更多考试。</p>
              </div>
            </form>
            <div className="mt-5 border-t border-sage-100 pt-4">
              <Link to="/" className="text-sm font-medium text-primary hover:text-primary-hover">
                返回首页
              </Link>
            </div>
          </CardBody>
        </Card>
        <BeianFooter className="mt-8" />
      </div>
    </div>
  );
}

export default RedeemPage;
